import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, UserPlus, Edit2, Trash2, Key, CheckCircle, XCircle } from 'lucide-react';
import DualScrollTable from '../../../components/common/DualScrollTable';
import { userService } from '../../../services/userService';
import { userEmailService } from '../../../services/supabase/user-email';
import EditUserModal from './modals/EditUserModal';
import DeleteUserModal from './modals/DeleteUserModal';
import ResetPasswordModal from './modals/ResetPasswordModal';
import type { User } from '../../../types/auth';
import { useAuth } from '../../../context/AuthContext';

export default function UserManagement() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [modalState, setModalState] = useState<'edit' | 'delete' | 'reset' | null>(null);
  const [isLoadingEmail, setIsLoadingEmail] = useState(false);
  
  // Redirect non-admin users
  if (!user || user.role !== 'system_admin') {
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-semibold text-red-600">Access Denied</h2>
        <p className="mt-2">Only system administrators can manage users.</p>
      </div>
    );
  }

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: userService.getAllUsers
  });

  const createUserMutation = useMutation({
    mutationFn: userService.createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setModalState(null);
    }
  });

  const updateUserMutation = useMutation({
    mutationFn: userService.updateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setModalState(null);
    }
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => userService.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setModalState(null);
    }
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ userId, newPassword, email }: { userId: string; newPassword: string; email: string }) => 
      userService.resetPassword({ userId, newPassword, email }),
    onSuccess: () => {
      setModalState(null);
    }
  });

  const filteredUsers = users.filter(user => 
    user.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div>
      <div className="mb-6">
        <div className="w-full">
          <input
            type="text"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-4 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden rounded-lg">
          <DualScrollTable>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {user.role.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {user.isActive ? (
                          <>
                            <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                            <span className="text-green-800">Active</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="h-5 w-5 text-red-500 mr-2" />
                            <span className="text-red-800">Inactive</span>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DualScrollTable>
        </div>
      )}

      {modalState === 'edit' && (
        <EditUserModal
          user={selectedUser}
          onClose={() => {
            setSelectedUser(null);
            setModalState(null);
          }}
          onSave={async (userData: User & { password?: string }) => {
            if (selectedUser) {
              await updateUserMutation.mutateAsync(userData);
            } else {
              await createUserMutation.mutateAsync(userData as User & { password: string });
            }
          }}
        />
      )}

      {modalState === 'delete' && selectedUser && (
        <DeleteUserModal
          user={selectedUser}
          onClose={() => {
            setSelectedUser(null);
            setModalState(null);
          }}
          onConfirm={async () => {
            await deleteUserMutation.mutateAsync(selectedUser.id);
          }}
        />
      )}

      {modalState === 'reset' && selectedUser && (
        <ResetPasswordModal
          user={selectedUser}
          onClose={() => {
            setSelectedUser(null);
            setModalState(null);
          }}
          onConfirm={async (newPassword: string) => {
            await resetPasswordMutation.mutateAsync({
              userId: selectedUser.id,
              newPassword,
              email: selectedUser.email || ''
            });
          }}
        />
      )}
    </div>
  );
}
